// src/components/RatingsViewModal.js
import React, { useState, useEffect } from 'react';
import { getUserRatings, getUserRatingStats } from '../api/ratings';
import './RatingsViewModal.css';

export default function RatingsViewModal({ show, onClose, userId, userName }) {
  const [ratings, setRatings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function loadRatings() {
      try {
        setLoading(true);
        setError(null);
        const [ratingsData, statsData] = await Promise.all([
          getUserRatings(userId, 0, 50),
          getUserRatingStats(userId)
        ]);
        setRatings(ratingsData);
        setStats(statsData);
      } catch (err) {
        console.error('Error loading ratings:', err);
        setError('Failed to load ratings. Please try again.');
      } finally {
        setLoading(false);
      }
    }

    if (show && userId) {
      loadRatings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, userId]);

  if (!show) return null;

  return (
    <div className="ratings-modal-overlay" onClick={onClose}>
      <div className="ratings-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="ratings-modal-header">
          <h2>⭐ Ratings for {userName}</h2>
          <button className="ratings-modal-close" onClick={onClose}>×</button>
        </div>

        {loading ? (
          <div className="ratings-loading">
            <div className="loading-spinner"></div>
            <p>Loading ratings...</p>
          </div>
        ) : error ? (
          <div className="ratings-error">
            <p>{error}</p>
            <button className="retry-btn" onClick={() => {
              if (userId) {
                setLoading(true);
                setError(null);
                async function retry() {
                  try {
                    const [ratingsData, statsData] = await Promise.all([
                      getUserRatings(userId, 0, 50),
                      getUserRatingStats(userId)
                    ]);
                    setRatings(ratingsData);
                    setStats(statsData);
                  } catch (err) {
                    console.error('Error loading ratings:', err);
                    setError('Failed to load ratings. Please try again.');
                  } finally {
                    setLoading(false);
                  }
                }
                retry();
              }
            }}>Retry</button>
          </div>
        ) : (
          <>
            {/* Rating Statistics */}
            {stats && (
              <div className="ratings-stats-section">
                <div className="stats-overview">
                  <div className="stats-main">
                    <div className="average-rating-large">
                      {stats.average_rating > 0 ? stats.average_rating.toFixed(1) : '0.0'}
                    </div>
                    <div className="stars-large">
                      {'⭐'.repeat(Math.round(stats.average_rating))}
                      {'☆'.repeat(5 - Math.round(stats.average_rating))}
                    </div>
                    <div className="total-ratings-text">
                      Based on {stats.total_ratings} {stats.total_ratings === 1 ? 'review' : 'reviews'}
                    </div>
                  </div>
                  
                  {/* Rating Breakdown */}
                  {stats.rating_breakdown && (
                    <div className="rating-breakdown">
                      {[5, 4, 3, 2, 1].map(star => {
                        const count = stats.rating_breakdown[star] || 0;
                        const percentage = stats.total_ratings > 0 
                          ? (count / stats.total_ratings * 100).toFixed(0) 
                          : 0;
                        return (
                          <div key={star} className="breakdown-row">
                            <span className="star-label">{star} ⭐</span>
                            <div className="breakdown-bar">
                              <div 
                                className="breakdown-fill" 
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                            <span className="breakdown-count">{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Individual Ratings */}
            <div className="ratings-list-section">
              <h3 className="ratings-list-title">Individual Reviews ({ratings.length})</h3>
              
              {ratings.length === 0 ? (
                <div className="no-ratings">
                  <div className="no-ratings-icon">💭</div>
                  <p>No reviews yet</p>
                </div>
              ) : (
                <div className="ratings-list">
                  {ratings.map((rating) => (
                    <div key={rating.id} className="rating-item">
                      <div className="rating-item-header">
                        <div className="rater-info">
                          {rating.rater?.personal_photo ? (
                            <img 
                              src={`https://gowithmebuddy-backend.onrender.com${rating.rater.personal_photo}`}
                              alt={rating.rater?.name}
                              className="rater-avatar"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            className="rater-avatar-fallback"
                            style={{
                              display: rating.rater?.personal_photo ? 'none' : 'flex'
                            }}
                          >
                            {(rating.rater?.name || 'U').charAt(0).toUpperCase()}
                          </div>
                          <div className="rater-details">
                            <div className="rater-name">{rating.rater?.name || 'Anonymous'}</div>
                            <div className="rater-role-badge">{rating.rater?.role || 'user'}</div>
                          </div>
                        </div>
                        <div className="rating-value-section">
                          <div className="rating-stars">
                            {'⭐'.repeat(rating.rating)}
                            {'☆'.repeat(5 - rating.rating)}
                          </div>
                          <div className="rating-date">
                            {new Date(rating.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </div>
                        </div>
                      </div>
                      
                      {rating.feedback_message && (
                        <div className="rating-feedback">
                          <div className="feedback-icon">💬</div>
                          <p className="feedback-text">{rating.feedback_message}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        <div className="ratings-modal-footer">
          <button className="close-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
